import { CONTENT_WIDTH, bottomMarginItemGlobal } from "../../utils/pdfConstants";
import { buildBucketedRows, buildImmediateRows, getArea } from "../../PTS/toimenpiteetTekstimuodossaUtils";

// Same single-composited-canvas-image technique as every PDF block with a
// chart/table in this app. Unlike Capex-yhteenveto (always a small
// fixed-size dashboard), this block's row count is unbounded (up to 4 PTS
// categories × 11 years × many items each), so it reuses
// RenderMaaraaikaistarkastusTaulukko.js's exact chunking algorithm instead
// of Capex-yhteenveto's single-image approach — see that file's own
// comment for why (an image taller than one page doesn't paginate; pdfmake
// treats it as one atomic block).

const fmtEur = (v) => `${Math.round(v).toLocaleString("fi-FI")} €`;

const IMG_WIDTH = 1080;
const PAD_X = 0; // flush to content edges, same reasoning as the other table renderer
const PAD_Y = 8;
const USABLE_W = IMG_WIDTH - PAD_X * 2;

// Only "Kohde" needs real wrap budget — far fewer/simpler columns than
// Määräaikaistarkastustaulukko's 9, so widths don't need to fight for space.
const COL_LABELS = ["Vuosi", "Kategoria", "Kohde", "Kustannus (€)"];
const VUOSI_W = 70;
const KATEGORIA_W = 170;
const KUSTANNUS_W = 160;
const KOHDE_W = USABLE_W - VUOSI_W - KATEGORIA_W - KUSTANNUS_W;
const COL_WIDTHS = [VUOSI_W, KATEGORIA_W, KOHDE_W, KUSTANNUS_W];

const CELL_PAD_X = 8;
const CELL_PAD_Y = 7;
const COL_GUTTER = 6;
const LINE_H = 19; // scaled alongside the table font size (12→14→17px)

const MAX_CHUNK_H = Math.round(700 * (IMG_WIDTH / CONTENT_WIDTH));
const BUCKET_TITLE_H = 50;
const SUBTOTAL_H = 36;
// Blank space before each new bucket's heading, separating it from the
// previous bucket's subtotal bar directly above it.
const BUCKET_GAP = 28;

function wrapLines(ctx, text, maxWidth, font) {
  ctx.font = font;
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(test).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function measureHeader(ctx) {
  const font = "bold 17px Lato, sans-serif";
  let maxLines = 1;
  const lines = COL_LABELS.map((label, i) => {
    const l = wrapLines(ctx, label, COL_WIDTHS[i] - CELL_PAD_X * 2 - COL_GUTTER, font);
    maxLines = Math.max(maxLines, l.length);
    return l;
  });
  return { lines, height: maxLines * LINE_H + CELL_PAD_Y * 2 };
}

function measureRow(ctx, row) {
  const costText = fmtEur(row.costEur);
  const vuosiLines = wrapLines(ctx, String(row.year ?? ""), VUOSI_W - CELL_PAD_X * 2 - COL_GUTTER, "17px Lato, sans-serif");
  const kategoriaLines = wrapLines(ctx, row.category || "", KATEGORIA_W - CELL_PAD_X * 2 - COL_GUTTER, "17px Lato, sans-serif");
  const kohdeLines = wrapLines(ctx, row.kohde || "", KOHDE_W - CELL_PAD_X * 2 - COL_GUTTER, "17px Lato, sans-serif");
  const kustannusLines = wrapLines(ctx, costText, KUSTANNUS_W - CELL_PAD_X * 2 - COL_GUTTER, "17px Lato, sans-serif");

  const maxLines = Math.max(vuosiLines.length, kategoriaLines.length, kohdeLines.length, kustannusLines.length);
  const height = Math.max(28, maxLines * LINE_H) + CELL_PAD_Y * 2;

  return { height, lines: { vuosiLines, kategoriaLines, kohdeLines, kustannusLines } };
}

function drawTableHeader(ctx, x, y, headerPlan) {
  // Same green as the page's own top banner (MakeDocDefinitionFromJson.js's
  // header()) and the sibling PTS renderers (renderYhteenvetoPtsBlock.js's
  // COLORS.green) — #198754 is a different, mismatched shade. No bottom
  // stroke, matching every other colored-bar header in this app.
  ctx.fillStyle = "#04aa00";
  ctx.fillRect(x, y, USABLE_W, headerPlan.height);
  ctx.textAlign = "left";
  ctx.fillStyle = "#ffffff";
  let cx = x;
  headerPlan.lines.forEach((lines, i) => {
    lines.forEach((line, li) => {
      ctx.font = "bold 17px Lato, sans-serif";
      const align = i === 3 ? "right" : "left";
      ctx.textAlign = align;
      ctx.fillText(line, align === "right" ? cx + COL_WIDTHS[i] - CELL_PAD_X : cx + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
    });
    cx += COL_WIDTHS[i];
  });
  return headerPlan.height;
}

// Plain bold heading, not a colored bar — matches the reference table's
// own "1.5 Long term (year 6-10)" style section headings above each table.
function drawBucketTitleBar(ctx, x, y, text, height) {
  ctx.textAlign = "left";
  ctx.fillStyle = "#1c1c1e";
  ctx.font = "bold 22px Lato, sans-serif";
  ctx.fillText(text, x + CELL_PAD_X, y + height / 2 + 8);
}

function drawSubtotalBar(ctx, x, y, label, amountEur, height) {
  ctx.fillStyle = "#f1f3f5";
  ctx.fillRect(x, y, USABLE_W, height);
  ctx.fillStyle = "#495057";
  ctx.font = "bold 17px Lato, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(label, x + CELL_PAD_X, y + height / 2 + 4);
  ctx.textAlign = "right";
  ctx.fillText(fmtEur(amountEur), x + USABLE_W - CELL_PAD_X, y + height / 2 + 4);
}

function drawRow(ctx, x, y, lines, height) {
  ctx.fillStyle = "#1c1c1e";
  ctx.font = "17px Lato, sans-serif";
  let cx = x;

  const col = (colLines, colWidth, align = "left") => {
    ctx.textAlign = align;
    colLines.forEach((line, li) => {
      const tx = align === "right" ? cx + colWidth - CELL_PAD_X : cx + CELL_PAD_X;
      ctx.fillText(line, tx, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
    });
    cx += colWidth;
  };

  col(lines.vuosiLines, VUOSI_W);
  col(lines.kategoriaLines, KATEGORIA_W);
  col(lines.kohdeLines, KOHDE_W);
  col(lines.kustannusLines, KUSTANNUS_W, "right");

  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x + USABLE_W, y + height);
  ctx.stroke();
}

function buildChunkImage(entries, headerPlan) {
  const contentH = entries.reduce((sum, e) => sum + e.height, 0);
  const totalH = PAD_Y * 2 + contentH;

  const canvas = document.createElement("canvas");
  canvas.width = IMG_WIDTH;
  canvas.height = totalH;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let cursorY = PAD_Y;

  for (const entry of entries) {
    if (entry.type === "bucketTitle") {
      // Heading + its own column header, repeated per bucket (not once per
      // page) — matches the reference table's own layout, where every
      // "1.x ..." section has its own YEAR/CATEGORY/ITEM/COST header
      // directly beneath it.
      // BUCKET_GAP of blank space before the heading itself — separates
      // this bucket's table from the previous one's subtotal bar.
      drawBucketTitleBar(ctx, PAD_X, cursorY + BUCKET_GAP, entry.text, BUCKET_TITLE_H);
      drawTableHeader(ctx, PAD_X, cursorY + BUCKET_GAP + BUCKET_TITLE_H, headerPlan);
    } else if (entry.type === "headerOnly") {
      // Mid-bucket chunk break — just repeat the column header, no "(jatkuu)"
      // heading above it.
      drawTableHeader(ctx, PAD_X, cursorY, headerPlan);
    } else if (entry.type === "subtotal") {
      drawSubtotalBar(ctx, PAD_X, cursorY, entry.label, entry.amountEur, entry.height);
    } else {
      drawRow(ctx, PAD_X, cursorY, entry.lines, entry.height);
    }
    cursorY += entry.height;
  }

  return canvas.toDataURL("image/png");
}

// "Välittömästi / ennen kauppaa" — a short 2-column (Kategoria, Kohde) list,
// no year/cost columns and no chunking pipeline: these are urgent/pre-sale
// items, realistically few, so one composited image (same approach
// CapexYhteenveto.jsx uses for its own naturally-bounded content) is enough
// — unlike the year-bucketed tables below, whose row count is unbounded.
const IMM_KATEGORIA_W = 260;
const IMM_KOHDE_W = USABLE_W - IMM_KATEGORIA_W;

function buildImmediateListImage(ctx, rows, label) {
  const font = "17px Lato, sans-serif";
  const headerFont = "bold 17px Lato, sans-serif";
  const titleH = BUCKET_TITLE_H;

  const rowPlans = rows.map((r) => {
    const kategoriaLines = wrapLines(ctx, r.category || "", IMM_KATEGORIA_W - CELL_PAD_X * 2 - COL_GUTTER, font);
    const kohdeLines = wrapLines(ctx, r.kohde || "", IMM_KOHDE_W - CELL_PAD_X * 2 - COL_GUTTER, font);
    const height = Math.max(28, Math.max(kategoriaLines.length, kohdeLines.length) * LINE_H) + CELL_PAD_Y * 2;
    return { kategoriaLines, kohdeLines, height };
  });

  const headerH = LINE_H + CELL_PAD_Y * 2;
  const totalH = PAD_Y * 2 + BUCKET_GAP + titleH + headerH + rowPlans.reduce((sum, r) => sum + r.height, 0);

  const canvas = document.createElement("canvas");
  canvas.width = IMG_WIDTH;
  canvas.height = totalH;
  const c = canvas.getContext("2d");
  c.fillStyle = "#ffffff";
  c.fillRect(0, 0, canvas.width, canvas.height);

  let y = PAD_Y;
  drawBucketTitleBar(c, PAD_X, y + BUCKET_GAP, label, titleH);
  y += BUCKET_GAP + titleH;

  c.fillStyle = "#04aa00";
  c.fillRect(PAD_X, y, USABLE_W, headerH);
  c.fillStyle = "#ffffff";
  c.textAlign = "left";
  c.font = headerFont;
  c.fillText("Kategoria", PAD_X + CELL_PAD_X, y + CELL_PAD_Y + LINE_H - 3);
  c.fillText("Kohde", PAD_X + IMM_KATEGORIA_W + CELL_PAD_X, y + CELL_PAD_Y + LINE_H - 3);
  y += headerH;

  c.fillStyle = "#1c1c1e";
  c.font = font;
  rowPlans.forEach((r) => {
    c.textAlign = "left";
    r.kategoriaLines.forEach((line, li) => c.fillText(line, PAD_X + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3));
    r.kohdeLines.forEach((line, li) => c.fillText(line, PAD_X + IMM_KATEGORIA_W + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3));

    c.strokeStyle = "#e5e7eb";
    c.lineWidth = 1;
    c.beginPath();
    c.moveTo(PAD_X, y + r.height);
    c.lineTo(PAD_X + USABLE_W, y + r.height);
    c.stroke();

    y += r.height;
  });

  return canvas.toDataURL("image/png");
}

function buildGrandTotalImage({ grandTotalEur, perM2Over10y, grossArea, alvLabel }) {
  const H = 132;
  const canvas = document.createElement("canvas");
  canvas.width = IMG_WIDTH;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#eafbf1";
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(0, 0, IMG_WIDTH, H, 10);
  else ctx.rect(0, 0, IMG_WIDTH, H);
  ctx.fill();

  ctx.textAlign = "left";
  ctx.fillStyle = "#04aa00";
  ctx.font = "bold 24px Lato, sans-serif";
  ctx.fillText(`Toimenpiteiden kustannusarvio yhteensä (10 vuotta): ${fmtEur(grandTotalEur)}`, 24, 58);

  ctx.fillStyle = "#6b7569";
  ctx.font = "16px Lato, sans-serif";
  ctx.fillText(
    `${fmtEur(perM2Over10y)}/m² (10 vuotta), ${grossArea.toLocaleString("fi-FI")} m² kokonaisala, ${alvLabel}`,
    24, 91,
  );

  return canvas.toDataURL("image/png");
}

export function renderToimenpiteetTekstimuodossa({ ptsContext, content, rakennusData, mergedRakennusData, contentWidth = CONTENT_WIDTH, alviton = false, groupedToimenpiteet }) {
  if (!ptsContext?.years?.length) return;

  // Label-only — every figure here is the same as entered in the PTS tables
  // (VAT-inclusive, buildBucketedRows applies no VAT math); this flag only
  // picks which phrasing is printed in the grand-total caption.
  const alvLabel = alviton ? "ALV 0 %" : "sis. ALV 25,5%";

  const { buckets, grandTotalEur } = buildBucketedRows(ptsContext);
  const immediateBucket = buildImmediateRows(groupedToimenpiteet);

  const measureCanvas = document.createElement("canvas");
  const mctx = measureCanvas.getContext("2d");

  // Leading, non-chunked list — only added when it actually has rows, same
  // rule as the screen component.
  if (immediateBucket.rows.length > 0) {
    content.push({
      image: buildImmediateListImage(mctx, immediateBucket.rows, immediateBucket.label),
      width: contentWidth,
      margin: [0, 0, 0, 4],
    });
  }

  const headerPlan = measureHeader(mctx);
  // Every bucketTitle entry draws its own heading AND its own column header
  // right after it (see buildChunkImage), so its height budget must include
  // both — this is what makes the header repeat per bucket, not once per page.
  const bucketTitleEntryH = BUCKET_GAP + BUCKET_TITLE_H + headerPlan.height;

  // Flatten bucket -> (bucketTitle, rows..., subtotal) into entries in
  // document order, each pre-measured, same shape as
  // RenderMaaraaikaistarkastusTaulukko.js's own allEntries.
  const allEntries = [];
  buckets.forEach((bucket) => {
    allEntries.push({ type: "bucketTitle", text: bucket.label, height: bucketTitleEntryH });
    bucket.rows.forEach((row) => {
      const { height, lines } = measureRow(mctx, row);
      allEntries.push({ type: "row", lines, height });
    });
    allEntries.push({ type: "subtotal", label: `Osasumma — ${bucket.label}`, amountEur: bucket.subtotalEur, height: SUBTOTAL_H });
  });

  // Same chunking loop as RenderMaaraaikaistarkastusTaulukko.js: no forced
  // pageBreak between chunks (pdfmake's own auto-flow already handles it).
  // If a bucket's rows span a chunk boundary, the new chunk just repeats
  // the column header (no heading/label above it) — no implicit per-chunk
  // header budget otherwise, since every chunk's first entry is always
  // either a bucketTitle or this header-only marker, each already covering
  // its own header cost.
  const chunks = [];
  let current = [];
  let currentH = 0;
  let inBucket = false;

  for (const entry of allEntries) {
    if (entry.type === "bucketTitle") inBucket = true;

    const wouldFit = currentH + entry.height <= MAX_CHUNK_H;
    if (!wouldFit && current.length) {
      chunks.push(current);
      current = [];
      currentH = 0;
      if (entry.type === "row" && inBucket) {
        const cont = { type: "headerOnly", height: headerPlan.height };
        current.push(cont);
        currentH += cont.height;
      }
    }
    current.push(entry);
    currentH += entry.height;
  }
  if (current.length) chunks.push(current);

  chunks.forEach((entries, idx) => {
    const imageBase64 = buildChunkImage(entries, headerPlan);
    content.push({
      image: imageBase64,
      width: contentWidth,
      margin: [0, 0, 0, 4],
    });
  });

  const grossArea = getArea(rakennusData, mergedRakennusData, "kokonaisala");
  const perM2Over10y = grandTotalEur / grossArea;
  content.push({
    image: buildGrandTotalImage({ grandTotalEur, perM2Over10y, grossArea, alvLabel }),
    width: contentWidth,
    margin: [0, 0, 0, bottomMarginItemGlobal],
  });
}
